import React, { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BatteryCharging, ChevronRight, Gauge, RotateCcw, ShieldCheck, Truck, Zap } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import RegoSearch from '../components/RegoSearch';
import LazySection from '../components/LazySection';
import { useCatalogData } from '../hooks/useCatalogData';
import { useVehicle } from '../contexts/VehicleContext';
import { fitsVehicle } from '../lib/plate';
import { HOME_BRANDS, HOME_CATEGORIES } from '../config/site';

const TRUST = [
  [Truck, 'Fast dispatch', 'From our Sydney and Melbourne warehouses'],
  [ShieldCheck, 'Australian warranty', 'Official manufacturer warranty on every battery'],
  [RotateCcw, 'Easy returns', 'Clear returns and exchanges policy'],
  [Zap, 'Secure payments', 'Your payment details are protected'],
];

const Grid = ({ children }) => <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">{children}</div>;
const Skeletons = ({ n = 4 }) => [...Array(n)].map((_, i) => <div key={i} className="h-72 bg-white border border-gray-200 rounded-lg animate-pulse" />);

/** Mounted only when scrolled near, so the catalogue is not requested on first paint. */
const Featured = memo(() => {
  const { products, loading } = useCatalogData();
  const { vehicle } = useVehicle();
  const [tab, setTab] = useState('all');
  const batteries = useMemo(() => (products || []).filter((p) => p.categoryName === 'Batteries' && !/Charger|Jump|Accessor/.test(p.subCategoryName || '')), [products]);
  const list = useMemo(() => {
    let l = batteries;
    if (vehicle) { const f = l.filter((p) => fitsVehicle(p, vehicle)); if (f.length) l = f; }
    if (tab !== 'all') l = l.filter((p) => p.subCategoryName === tab);
    return l.slice(0, 8);
  }, [batteries, vehicle, tab]);
  const tabs = [['all', 'All'], ['Car & SUV Batteries', 'Car & SUV'], ['4WD & Commercial', '4WD'], ['AGM & Start-Stop', 'AGM']];
  return (
    <section className="py-10 sm:py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{vehicle ? 'Batteries for your vehicle' : 'Popular car batteries'}</h2>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {tabs.map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 rounded whitespace-nowrap transition text-sm ${tab === v ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>{l}</button>
            ))}
          </div>
        </div>
        <Grid>{loading && !list.length ? <Skeletons n={4} /> : list.map((p) => <ProductCard key={p.id} product={p} />)}</Grid>
        <div className="mt-6 text-center"><Link to="/products?category=Batteries" className="inline-flex items-center text-red-600 hover:text-red-700 font-semibold">Shop all batteries <ChevronRight size={18} /></Link></div>
      </div>
    </section>
  );
});

const Deals = memo(() => {
  const { products, loading } = useCatalogData();
  const deals = useMemo(() => (products || []).filter((p) => Array.isArray(p.badges) && (p.badges.includes('sale') || p.badges.includes('hot'))).slice(0, 4), [products]);
  if (!loading && !deals.length) return null;
  return (
    <section className="py-10 sm:py-12 bg-gradient-to-r from-red-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-full mb-3"><Gauge size={18} /><span className="font-bold text-sm sm:text-base">Hot offers</span></div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Great value on top-selling parts</h2>
        </div>
        <Grid>{loading && !deals.length ? <Skeletons /> : deals.map((p) => <ProductCard key={p.id} product={p} />)}</Grid>
      </div>
    </section>
  );
});

const GUIDE = [
  ['Match the size', 'Battery size codes such as NS60, NS70 or DIN66 describe the case, terminal position and fit. Use your rego to see what suits your car, or check the size on your current battery.'],
  ['Check the cold cranking amps (CCA)', 'CCA is the starting power in cold weather. Choose a battery with at least the CCA your vehicle needs. More CCA gives extra margin, especially for diesels and 4WDs.'],
  ['Pick the right type', 'Standard calcium batteries suit most cars. Vehicles with start-stop or heavy electrical loads usually need an AGM battery. Replace like with like.'],
  ['Look at the warranty', 'Our batteries carry a manufacturer warranty from 24 to 48 months. Longer warranty periods are a good sign of build quality.'],
];

const Home = () => (
  <div className="bg-gray-50">
    <section className="bg-gradient-to-r from-gray-100 to-gray-200 py-8 sm:py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Find the right battery for your car</h1>
          <p className="text-base sm:text-lg text-gray-600">Enter your rego and state to see batteries and parts that fit.</p>
        </div>
        <RegoSearch />
      </div>
    </section>

    <section className="py-6 sm:py-8 bg-white border-b">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
          {TRUST.map(([Icon, t, d]) => (
            <div key={t} className="flex flex-col items-center">
              <Icon size={32} className="text-red-600 mb-2" strokeWidth={1.5} />
              <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-0.5">{t}</h3>
              <p className="text-xs sm:text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-10 sm:py-12 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Shop by category</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {HOME_CATEGORIES.map((c, i) => (
            <Link key={c.name} to={c.to} className="relative group overflow-hidden rounded-lg shadow h-32 sm:h-44 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 text-white p-4 sm:p-6 flex flex-col justify-end">
              <span className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-red-600/25 group-hover:scale-125 transition-transform duration-300" />
              {i === 0 && <BatteryCharging size={34} className="absolute top-4 right-4 text-white/30" />}
              <h3 className="text-base sm:text-xl font-bold leading-tight">{c.name}</h3>
              <p className="text-xs sm:text-sm text-gray-300 hidden sm:block">{c.blurb}</p>
              <span className="mt-1 flex items-center text-xs sm:text-sm font-semibold">Shop all <ChevronRight size={14} className="ml-0.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <LazySection minHeight={520}><Featured /></LazySection>
    <LazySection minHeight={420}><Deals /></LazySection>

    <LazySection minHeight={300}>
      <section className="py-10 sm:py-12 bg-white border-y">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">How to choose a car battery</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {GUIDE.map(([t, d], i) => (
              <div key={t} className="border border-gray-200 rounded-lg p-5">
                <span className="inline-grid h-8 w-8 place-items-center rounded-full bg-red-50 text-red-600 font-bold mb-3">{i + 1}</span>
                <h3 className="font-bold text-gray-900 mb-1.5">{t}</h3>
                <p className="text-sm text-gray-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </LazySection>

    <LazySection minHeight={200}>
      <section className="py-10 sm:py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Brands we stock</h2>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 sm:gap-4">
            {HOME_BRANDS.map((b) => (
              <Link key={b} to={`/products?brand=${encodeURIComponent(b)}`} className="flex items-center justify-center p-3 sm:p-4 border border-gray-200 rounded hover:border-red-600 hover:shadow-md transition bg-white text-sm sm:text-base font-semibold text-gray-700 hover:text-red-600">{b}</Link>
            ))}
          </div>
        </div>
      </section>
    </LazySection>
  </div>
);
export default Home;
